import { IConfigImageCompositor } from "../../../services/api/3d/CompositorImageApi/type";
import { StatusName } from "../../../services/api/type";
import { CompositorImageService } from "../../../services/CompositorImageService/CompositorImageService";
import { ConfigurationService } from "../../../services/ConfigurationService/ConfigurationService";
import { getDataProductForGetImageById } from "../../reducers/ui/selectors/imageProductSelectors";
import {
  getProductByIdAndPath,
  getCurrentCategoryFolder,
  getProductFromCache,
} from "../../reducers/ui/selectors/objectSelectors";
import { updateProcessObjectImage } from "../../reducers/ui/uiReducer";
import { ISelectItemObjectData } from "../../reducers/ui/uiTypes";
import { AppStore } from "../../store";

export const loadImageProduct =
  (
    productId: string,
    selectData: ISelectItemObjectData,
    prevProductId: string | null = null
  ) =>
  async (store: AppStore): Promise<Partial<ISelectItemObjectData> | null> => {
    const state = store.getState();

    // First try to get product from currentCategoryFolder (has fetched products)
    const currentCategoryFolder = getCurrentCategoryFolder(state);
    const projectInFolder = currentCategoryFolder?.projects?.find(
      p => p.id === selectData.projectId
    );
    const productFromFolder = projectInFolder?.availableProducts?.find(
      p => p.id === selectData.realId
    );

    // Try cache by project
    const productFromCache = getProductFromCache(
      selectData.projectId,
      selectData.realId
    )(state);

    // Fallback to path-based lookup
    const productFromPath = getProductByIdAndPath(
      selectData.realId,
      selectData.pathObjectIds,
      selectData.projectId
    )(state);

    const product = productFromFolder || productFromCache || productFromPath;

    if (!product) {
      return null;
    }

    const configData = getDataProductForGetImageById(
      productId,
      selectData
    )(state);

    // Align camera dimensions in request with UI settings (desired image size)
    const uiCamera = state.ui.camera;

    if (configData?.config?.camera) {
      // If using ConfigurationService (non-layered), prefer renderWidth/renderHeight
      if (!product.layeredRender) {
        (configData.config.camera as any).renderWidth = uiCamera.imageWidth;
        (configData.config.camera as any).renderHeight = uiCamera.imageHeight;
      } else {
        (configData.config.camera as any).imageWidth = uiCamera.imageWidth;
        (configData.config.camera as any).imageHeight = uiCamera.imageHeight;
      }

      // Optionally keep imageType consistent if available
      if (
        (uiCamera as any).imageType &&
        (configData.config.camera as any).imageType !== undefined
      ) {
        (configData.config.camera as any).imageType = (
          uiCamera as any
        ).imageType;
      }
    }

    console.log("camera", uiCamera);
    console.log("camera", configData);

    // Mark image render as pending to show loader immediately
    store.dispatch(
      updateProcessObjectImage({
        imageHash: "pending",
        id: productId,
        selectData,
        prevProductId,
      })
    );

    const imageUrl = await renderImageByConfigData(
      configData,
      product.layeredRender,
      productId,
      selectData,
      prevProductId,
      { width: uiCamera.imageWidth, height: uiCamera.imageHeight }
    )(store);

    if (imageUrl) {
      // Clear pending state on immediate success
      store.dispatch(
        updateProcessObjectImage({
          imageHash: null,
          id: null,
          selectData: null,
          prevProductId: null,
        })
      );
      return {
        previewImage: imageUrl,
      };
    }

    return null;
  };

function renderImageByConfigData(
  configData: IConfigImageCompositor,
  layeredRender: boolean,
  productId: string,
  selectData: ISelectItemObjectData,
  prevProductId: string | null = null,
  dims?: { width: number; height: number }
) {
  return async (store: AppStore) => {
    if (layeredRender) {
      return new CompositorImageService().getCompositorImage({
        height: dims?.height ?? 400,
        width: dims?.width ?? 400,
        config: configData,
      });
    } else {
      const data = await new ConfigurationService().getConfigurationImage(
        configData
      );

      if (data.status === StatusName.SUCCESS && data.url) {
        return data.url;
      }

      if (data.status !== StatusName.SUCCESS && data.hash) {
        store.dispatch(
          updateProcessObjectImage({
            imageHash: data.hash,
            id: productId,
            selectData,
            prevProductId: prevProductId,
          })
        );

        // If there's a cached imageUrl even with failed/pending status, return it
        if (data.url) {
          return data.url;
        }
      }

      return null;
    }
  };
}
